import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'panel',
    'container',
    'card',
    'separator',
    'step',
    'stepName',
    'stepContainer',
    'arrow',
  ];

  static values = { current: { type: Number, default: 0 } };

  connect() {
    this._locked = false;
    this._scrollAccumulator = 0;

    this._observer = new IntersectionObserver(
      ([entry]) => { this._visible = entry.isIntersecting },
      { threshold: 0.3 }
    );
    this._observer.observe(this.element);

    this._onWheel = this.onWheel.bind(this);
    window.addEventListener('wheel', this._onWheel, { passive: false });

    this._onKeyDown = this.onKeyDown.bind(this);

    this._onPointerDown = this.onPointerDown.bind(this);
    this._onPointerMove = this.onPointerMove.bind(this);
    this._onPointerUp = this.onPointerUp.bind(this);

    this._onTouchStart = this.onTouchStart.bind(this);
    this._onTouchMove = this.onTouchMove.bind(this);
    this._onTouchEnd = this.onTouchEnd.bind(this);

    window.addEventListener('keydown', this._onKeyDown);

    window.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp);

    window.addEventListener('touchstart', this._onTouchStart, { passive: true });
    window.addEventListener('touchmove', this._onTouchMove, { passive: false });
    window.addEventListener('touchend', this._onTouchEnd, { passive: true });

    this._show(0);
  }

  disconnect() {
    window.removeEventListener('wheel', this._onWheel);

    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('pointerdown', this._onPointerDown);
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);

    window.removeEventListener('touchstart', this._onTouchStart);
    window.removeEventListener('touchmove', this._onTouchMove);
    window.removeEventListener('touchend', this._onTouchEnd);

    this._observer.disconnect();
  }

  onWheel(e) {
    if (!this._visible) return;

    const { currentValue: current, cardTargets: { length: total } } = this;
    if ((e.deltaY > 0 && current >= total - 1) || (e.deltaY < 0 && current <= 0)) return;

    e.preventDefault();

    if (this._locked) return;

    this._scrollAccumulator += e.deltaY;

    // Reset accumulator if scroll pauses (clears trackpad inertia between gestures)
    clearTimeout(this._accumTimeout);
    this._accumTimeout = setTimeout(() => { this._scrollAccumulator = 0; }, 100);

    if (Math.abs(this._scrollAccumulator) < 50) return;

    this._scrollAccumulator = 0;
    this._locked = true;
    this._goTo(current + Math.sign(e.deltaY));

    // Lock for the duration of the CSS transition
    setTimeout(() => { this._locked = false; }, 600);
  }

  onKeyDown(e) {
    if (!this._visible || this._locked) return;

    const { currentValue: current, cardTargets: { length: total } } = this;
    let direction = 0;

    if ((e.key === 'ArrowDown' || e.key === 'PageDown') && current < total - 1) {
      direction = 1;
    } else if ((e.key === 'ArrowUp' || e.key === 'PageUp') && current > 0) {
      direction = -1;
    }

    if (direction === 0) return;

    e.preventDefault();
    this._locked = true;
    this._goTo(current + direction);
    setTimeout(() => { this._locked = false }, 600);
  }

  onPointerDown(e) {
    if (!this._visible || e.pointerType === 'mouse') return;

    this._pointerStartY = e.clientY;
    this._pointerHandled = false;
  }

  onPointerMove(e) {
    if (!this._visible || this._pointerHandled || this._pointerStartY === null) return;
    if (e.pointerType === 'mouse') return;

    const delta = this._pointerStartY - e.clientY;
    const { currentValue: current, cardTargets: { length: total } } = this;

    if ((delta > 0 && current >= total - 1) || (delta < 0 && current <= 0)) return;

    if (Math.abs(delta) > 50) {
      e.preventDefault();
      this._pointerHandled = true;
      this._goTo(current + Math.sign(delta));
    }
  }

  onPointerUp() {
    this._pointerStartY = null;
    this._pointerHandled = false;
  }

  onTouchStart(e) {
    if (!this._visible) return;

    this._touchStartY = e.touches[0].clientY;
    this._touchDelta = 0;
    this._touchHandled = false;
  }

  onTouchMove(e) {
    if (!this._visible || this._touchHandled) return;

    const currentY = e.touches[0].clientY;
    this._touchDelta = this._touchStartY - currentY; // positif = swipe up

    const { currentValue: current, cardTargets: { length: total } } = this;
    const atBoundary = (this._touchDelta > 0 && current >= total - 1) ||
                       (this._touchDelta < 0 && current <= 0);

    if (atBoundary) return;

    // Seuil de 50px pour déclencher le changement de slide
    if (Math.abs(this._touchDelta) > 50) {
      this._touchHandled = true;
      this._goTo(current + Math.sign(this._touchDelta));
    }
  }

  onTouchEnd() {
    this._touchStartY = 0;
    this._touchDelta = 0;
    this._touchHandled = false;
  }

  _goTo(index) {
    this.currentValue = index;
    this._show(index);
  }

  _show(index) {
    const expanded = index > 0;

    this.cardTargets.forEach((card, i) => {
      card.classList.toggle('past', i < index);
      card.classList.toggle('active', i === index);
    });

    this.panelTarget.classList.toggle('expanded', expanded);
    if (this.hasSeparatorTarget) this.separatorTarget.classList.toggle('full-height', expanded);

    if (this.hasStepTarget) this.stepTarget.textContent = `0${index + 2}`;
    if (this.hasStepContainerTarget) this.stepContainerTarget.classList.toggle('d-none', index >= 2);
    if (this.hasStepContainerTarget && index > 0) this.stepContainerTarget.classList.remove('fade-in');
    this.stepNameTargets.forEach((el, i) => el.classList.toggle('d-none', i !== index));

    if (this.hasArrowTarget) this.arrowTarget.classList.toggle('disabled', index >= 2);
  }
}
